import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, Package, ArrowRight } from 'lucide-react';
import api from '../utils/api';

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  
  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id'); // For Stripe

  useEffect(() => {
    if (sessionId) {
      // Verify Stripe payment
      verifyStripePayment();
    } else if (orderId) {
      // Direct order lookup
      fetchOrderDetails();
    } else {
      setLoading(false);
      setError('No order information provided');
    }
    
    // Clean up pending Stripe order
    localStorage.removeItem('pending_stripe_order');
  }, [sessionId, orderId]);

  const verifyStripePayment = async () => {
    try {
      const response = await api.get(`/shop-payments/stripe/status/${sessionId}`);
      setOrderDetails({
        order_id: response.data.order_id,
        order_number: response.data.order_number,
        payment_status: response.data.payment_status,
        amount: response.data.amount_total,
        currency: response.data.currency
      });
    } catch (error) {
      console.error('Failed to verify payment:', error);
      setError('Failed to verify payment status');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await api.get(`/shop-payments/order/${orderId}`);
      setOrderDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
          <p className="text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            Go to Home <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center" data-testid="order-success-page">
      <div className="text-center max-w-lg">
        <div className="w-24 h-24 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center animate-bounce">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Payment Successful!</h1>
        
        <p className="text-xl text-muted-foreground mb-8">
          Thank you for your order. Your payment has been processed successfully.
        </p>
        
        {orderDetails && (
          <div className="glass-card p-6 mb-8 text-left">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" /> Order Details
            </h2>
            <div className="space-y-2">
              {orderDetails.order_number && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order Number</span>
                  <span className="font-mono font-bold">{orderDetails.order_number}</span>
                </div>
              )}
              {orderDetails.payment_status && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Status</span>
                  <span className={`font-semibold ${orderDetails.payment_status === 'paid' ? 'text-green-500' : 'text-yellow-500'}`}>
                    {orderDetails.payment_status === 'paid' ? 'Paid' : 'Processing'}
                  </span>
                </div>
              )}
              {orderDetails.amount && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <span className="font-bold text-primary">
                    {orderDetails.currency === 'inr' ? '₹' : '$'}{(orderDetails.amount / 100).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/my-orders" className="btn-primary inline-flex items-center justify-center gap-2">
            View My Orders <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/products" className="btn-secondary inline-flex items-center justify-center gap-2">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
